import { onScopeDispose } from 'vue';

/**
 * Ref-counted body scroll lock shared by every overlay (LgDrawer, LgModal).
 *
 * A per-instance boolean (`locked ? restore original : no-op`) breaks when
 * two overlays are open at once — e.g. AppShell's mobile-nav drawer and
 * LogsView's containers drawer both live in the tree, or a modal opens over
 * a drawer. Whichever one closes first would restore `overflow` even though
 * the other overlay still needs scroll locked, and the second one to close
 * would then have nothing (correct) to restore.
 *
 * This module-level counter fixes that: `lock()` increments and only sets
 * `overflow: hidden` on the 0→1 transition (capturing whatever inline
 * `overflow` was there before); `unlock()` decrements and only restores it on
 * the 1→0 transition. Overlays nest/overlap freely as long as each call site
 * pairs its own lock with its own unlock.
 */
let lockCount = 0;
let previousOverflow: string | null = null;

function lock() {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount++;
}

function unlock() {
  if (lockCount === 0) return; // unbalanced unlock — nothing to undo
  lockCount--;
  if (lockCount === 0) {
    document.body.style.overflow = previousOverflow ?? '';
    previousOverflow = null;
  }
}

/**
 * Per-consumer handle onto the shared counted lock. Each call site gets its
 * own `lock`/`unlock` pair that tracks whether *this* instance currently
 * holds the lock, so:
 *  - calling `lock()` twice in a row (e.g. from a re-triggered watcher)
 *    doesn't double-increment the shared counter, and
 *  - unmounting while still "open" (parent removes the component instead of
 *    setting `open` to false first) still releases this instance's hold via
 *    `onScopeDispose`, without touching other instances' locks.
 *
 * `onScopeDispose` (rather than `onBeforeUnmount`) so this also cleans up
 * correctly when used inside a bare `effectScope()` — e.g. in tests — not
 * just inside a full component instance.
 */
export function useScrollLock() {
  let held = false;

  function lockOnce() {
    if (held) return;
    held = true;
    lock();
  }

  function unlockOnce() {
    if (!held) return;
    held = false;
    unlock();
  }

  onScopeDispose(unlockOnce);

  return { lock: lockOnce, unlock: unlockOnce };
}
